import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CLAMPS_SIDE_LABEL, clampsLabel, clampsSide } from "@/lib/packingFields";

// Amber note on the packing tab of a clamps-only warranty order.
export function ClampsOnlyNote({ order }) {
  const { t } = useT();
  return (
    <div className="mb-4 rounded-md border-2 border-amber-400 bg-amber-50 px-4 py-3" data-testid="clamps-only-note">
      <div className="text-[11px] font-black uppercase tracking-widest text-amber-800">📦 {t("No own box")} · {t(CLAMPS_SIDE_LABEL[clampsSide(order)])}</div>
      <div className="text-xs text-amber-900 mt-1 leading-relaxed">
        {t("Clamps only — no covers, no box of their own. Confirm the clamp type & count, take a photo, then choose which order's box they travel in. The host order is tagged so the clamps are taken out at the next station.")}
      </div>
    </div>
  );
}

// BO / SA: which clamps the replacement actually contains (front / rear / both).
export function ClampsSideSelect({ order }) {
  const qc = useQueryClient();
  const { t } = useT();
  const m = useMutation({
    mutationFn: (side) => api.patch(`/orders/${order.id}/warranty-scope`, { clamps_side: side }),
    onSuccess: (r) => { qc.invalidateQueries(); toast.success(r?.data?.label || "Saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <select
      data-testid="clamps-side-select"
      value={clampsSide(order)}
      disabled={m.isPending}
      onChange={(e) => m.mutate(e.target.value)}
      className="h-[1.6rem] rounded-md border border-amber-500 bg-amber-50 text-amber-900 text-[0.6rem] font-bold px-1.5"
      title={t("Which clamps were produced for this warranty")}
    >
      {Object.entries(CLAMPS_SIDE_LABEL).map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}
    </select>
  );
}

// "SHIPS INSIDE BOX OF" — pick the PO whose box carries the clamps.
export function ShipsWithPicker({ order, mySignature, compact = false }) {
  const qc = useQueryClient();
  const { t } = useT();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const inside = order.ships_inside;
  const { data } = useQuery({
    queryKey: ["host-candidates", order.id, q],
    queryFn: async () => (await api.get(`/orders/${order.id}/host-candidates`, { params: { q } })).data,
    enabled: open && !inside?.order_id,
    staleTime: 15_000,
  });
  const set = useMutation({
    mutationFn: (hostId) => api.post(`/orders/${order.id}/ship-with`, { host_order_id: hostId, employee_signature: mySignature }),
    onSuccess: (r, hostId) => { qc.invalidateQueries(); setOpen(false); setQ(""); toast.success(hostId ? `${clampsLabel(order)} → ${t("inside")} ${r?.data?.ships_inside?.po_number}` : t("Host order cleared")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (inside?.order_id) {
    return (
      <div className={`flex items-center gap-2 flex-wrap ${compact ? "" : "mt-3"}`} data-testid="ships-with-selected">
        <span className="px-2 py-1 rounded bg-sky-600 text-white text-xs font-bold">📦 {t("INSIDE")} {inside.po_number}</span>
        <span className="text-[11px] text-slate-600">{t("set by")} {inside.set_by}</span>
        {!inside.separated && !order.packed_at && (
          <button type="button" data-testid="ships-with-change" onClick={(e) => { e.stopPropagation(); set.mutate(null); }} className="text-[11px] text-slate-500 underline hover:text-slate-800">
            {t("change")}
          </button>
        )}
      </div>
    );
  }
  const rows = data?.candidates || [];
  return (
    <div className={`relative ${compact ? "flex-1 min-w-[220px]" : "mt-3"}`} data-testid="ships-with-picker" onClick={(e) => e.stopPropagation()}>
      <input
        data-testid="ships-with-search"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={t("type a PO in Packing today…")}
        className="w-full h-9 rounded-md border border-sky-400 bg-white px-2 font-mono text-sm"
      />
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[320px] bg-white border border-sky-400 rounded-md shadow-lg overflow-hidden text-sm max-h-72 overflow-y-auto" data-testid="ships-with-list">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-mono bg-slate-50 flex justify-between">
            <span>{t("Orders in Packing · same destination first")}</span>
            <button type="button" onClick={() => setOpen(false)} className="hover:text-slate-800">✕</button>
          </div>
          {rows.length === 0 && <div className="px-3 py-3 text-xs text-slate-500">{t("No orders in Packing / Inv Packing / Shipping match")}</div>}
          {rows.map((r) => (
            <button
              key={r.id}
              type="button"
              data-testid={`ships-with-option-${r.po_number}`}
              disabled={set.isPending}
              onClick={() => set.mutate(r.id)}
              className="w-full px-3 py-2 flex items-center justify-between gap-2 hover:bg-sky-50 border-t border-slate-100 text-left"
            >
              <div className="min-w-0 truncate"><span className="font-mono font-bold">{r.po_number}</span> <span className="text-slate-500">· {r.brand} {r.model} · {r.color}</span>{r.carries > 0 && <span className="text-amber-700"> · +{r.carries}</span>}</div>
              <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${r.packed ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                {r.country === "US" ? "🇺🇸" : "🇨🇦"} {r.country} · {r.packed ? t("packed") : t(r.status === "inventory_packing" ? "inv packing" : r.status)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Host order: "this box also carries X for warranty PO-…" + release button.
export function CarriesBanner({ order, mySignature, canRemove }) {
  const qc = useQueryClient();
  const { t } = useT();
  const active = (order.carries || []).filter((c) => !c.removed);
  const removed = useMutation({
    mutationFn: (orderId) => api.post(`/orders/${order.id}/carried-removed`, { order_id: orderId, employee_signature: mySignature }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(t("Clamps taken out — handed to shipping")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (active.length === 0) return null;
  return (
    <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-3 text-sm" data-testid="carries-banner">
      {active.map((c) => (
        <div key={c.order_id} className="flex items-center gap-3 flex-wrap" data-testid={`carries-${c.po_number}`}>
          <div className="flex-1 min-w-[240px] text-xs text-amber-900">
            <b>⚠ {t("Extra item in this box")}:</b> {c.label} {t("for warranty")} <Link to={`/order/${c.order_id}`} className="font-mono font-bold underline">{c.po_number}</Link>{c.customer ? ` (${c.customer})` : ""}.{" "}
            <b>{t("Take them out before this box goes to the customer")}</b> {t("and ship them separately.")}
          </div>
          {canRemove && (
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs" disabled={removed.isPending} onClick={() => removed.mutate(c.order_id)} data-testid={`carried-removed-${c.po_number}`}>
              ✓ {t("Clamps removed & handed to shipping")}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
